import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, takeUntil, tap } from 'rxjs/operators';

import { LogType, ViAppService, ViLogger } from '@vi-iot/ui';

import { DocumentsUpdateComponent, UpgradeCommercialBannerComponent } from '@components';
import { GTMService, RoutingService, TranslationConfigService } from '@services';
import {
  AuthState,
  AuthStore,
  DocumentsStore,
  LanguageStore,
  ServiceTicketStore,
  SUPPORTED_LANGUAGE,
} from '@store';

import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { SubscriptionPlan } from '@vi-iot/client/index';
import { environment } from 'src/environments/environment';
import { viguideVersion } from '../../package-version';
import { DemoSettingsStore } from './@store/demo-settings/demo-settings.store';
import { LiveUpdatesDataService } from './shared/live-updates/live-updates-data.service';
import { LocalStorageKeys } from './shared/models/local-storage-keys.model';
import { PageVisibilityService } from './shared/page-visibility/page-visibility.service';
import { GTMEventModel } from './shared/services/gtm';
import { AppSwitcherFacade, AppsResponse } from '@vi-succ/vi-header-domain';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly newVersionToastKeys = {
    message: 'GENERAL.NEW_VERSION.MESSAGE',
    reloadBtn: 'GENERAL.NEW_VERSION.RELOAD_BTN',
    detailsBtn: 'GENERAL.NEW_VERSION.DETAILS',
  };
  headerType = 'home';
  searchIconVisible = true;
  shouldShowDocumentsDialog = true;
  isViGuideMonitoring = '';
  environment = environment;
  translationsLoaded = false;

  documentsDialogRef: MatDialogRef<DocumentsUpdateComponent>;
  bannerForCommercialFreeDialogRef: MatDialogRef<UpgradeCommercialBannerComponent>;
  authStoreState$: Observable<AuthState>;
  ngUnsubscribe$: Subject<void> = new Subject<void>();
  apps$: Observable<AppsResponse>;

  constructor(
    public authStore: AuthStore,
    public router: Router,
    public languageStore: LanguageStore,
    private translationConfig: TranslationConfigService,
    private appService: ViAppService,
    private translate: TranslateService,
    private documentsStore: DocumentsStore,
    private dialog: MatDialog,
    private viLogger: ViLogger,
    private gtmService: GTMService,
    private pageVisibilityService: PageVisibilityService,
    private serviceTicketStore: ServiceTicketStore,
    private liveUpdatesDataService: LiveUpdatesDataService,
    private routingService: RoutingService,
    private demoSettingsStore: DemoSettingsStore,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private appSwitcherService: AppSwitcherFacade
  ) {
    this.registerIcons();
    this.router.events.subscribe((routerEvent) => {
      this.checkEvent(routerEvent);
    });
  }

  ngOnInit(): void {
    this.languageStore.getLanguage(SUPPORTED_LANGUAGE[navigator.language.split('-')[1]]);
    this.translate.setDefaultLang(this.translationConfig.defaultLocale);
    this.translate.use(this.translationConfig.getLanguage());
    this.initTranslationsLoadedSubscription();
    this.initRouterEventsSubscription();
    this.checkNewVersion();
    this.initAuthStateSubscription();
    this.pageVisibilityService.initialize();
    this.initModalForCommercialFreeUser();
    this.functionsFiredForMaintainerOrUserHandler();
    this.authStore.startAuthFlow();

    // the isViGuideMonitoring flag is set by the ViGuide Mobile app while on the monitoring tab to hide the logout button on our side
    // this flag is read-only, setting and deleting it is on ViGuide Mobile's side
    // https://jira.viessmann-climatesolutions.com/browse/VIG-11615
    this.isViGuideMonitoring = localStorage.getItem(LocalStorageKeys.isViGuideMonitoring);

    const isDemoModeRunning = sessionStorage.getItem(LocalStorageKeys.isDemoModeRunning);
    if (isDemoModeRunning) {
      this.demoSettingsStore.triggerLoadDemoConfig();
      this.authStore.runDemo();
    }

    // Creates a stream which allows to defer url manipulation
    this.routingService.setIsNavigationFinished();
  }

  onLogout(): void {
    this.gtmService.track('onLogout');
    this.authStore.logout();
  }

  redirect(): void {
    const isUserOnStatusErrorPage = this.router.url.startsWith('status');

    if (!isUserOnStatusErrorPage) {
      this.router.navigate(['/home']);
      this.gtmService.track('onGoToPage', 'Home page', 'navbar');
    }
  }

  private initTranslationsLoadedSubscription(): void {
    //picked random translation
    this.translate.get('+INSTALLATIONS.INSTALLATION.GATEWAY_STATUS.STATUS.ONLINE').subscribe(() => {
      this.translationsLoaded = true;
    });
  }

  private initRouterEventsSubscription(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.searchIconVisible = event.url.startsWith('/home') ? false : true;
        this.headerType = this.getToolbarVersion(event.url);
        this.unsubscribeLiveUpdateForNonInstallationPages(event.url);
        this.shouldShowDocumentsDialog = !event.url.startsWith('/access-forbidden');
      });
  }

  private initModalForCommercialFreeUser(): void {
    this.authStore.isAuthorized$
      .pipe(
        filter((isAuthorized) => isAuthorized),
        switchMap(() =>
          combineLatest([
            this.router.events.pipe(filter((routerEvent) => routerEvent instanceof NavigationEnd)),
            this.authStore.selectDashboardFeatureIfAvailable$,
            this.authStore.companySubscriptionPlan$,
          ])
        ),
        filter(
          ([_event, isDashboardFeatureAvailable, subscriptionPlan]) =>
            !isDashboardFeatureAvailable && subscriptionPlan !== null
        ),
        distinctUntilChanged<[NavigationEnd, boolean, SubscriptionPlan]>(([event1], [event2]) => {
          return event1.url.split('?')[0] === event2.url.split('?')[0];
        }),
        filter(([event]) => !event.url.startsWith('/booking')),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(() => {
        this.dialog.open(UpgradeCommercialBannerComponent, {
          width: '792px',
          panelClass: 'upgrade-for-commercial-free-dialog',
          disableClose: true,
          data: {},
        });
      });
  }

  private checkNewVersion(): void {
    this.translate
      .get([
        this.newVersionToastKeys.message,
        this.newVersionToastKeys.reloadBtn,
        this.newVersionToastKeys.detailsBtn,
      ])
      .subscribe((translations: { [key: string]: string }) => {
        this.appService.checkNewVersion({
          message: translations[this.newVersionToastKeys.message],
          actions: [
            {
              id: 'reload-btn',
              raised: true,
              name: translations[this.newVersionToastKeys.reloadBtn],
              reload: true,
            },
            {
              id: 'details-btn',
              raised: true,
              name: translations[this.newVersionToastKeys.detailsBtn],
              navigateWithReload: '/updates',
            },
          ],
        });
      });
  }

  private getToolbarVersion(url: string): string {
    if (url === '/' || url === '/home' || url === '/access-forbidden') {
      return 'home';
    } else if (url === '/search' || url.includes('/users/')) {
      return 'rsa';
    } else {
      return 'full';
    }
  }

  private initAuthStateSubscription(): void {
    this.authStoreState$ = this.authStore.state$.pipe(
      distinctUntilChanged(isEqual),
      tap((state) => {
        this.apps$ = this.appSwitcherService.getApps(state?.me?.id);
      })
    );
  }

  private checkEvent(routerEvent: any): void {
    if (routerEvent instanceof NavigationStart) {
      this.viLogger.start('router-event-navigation', LogType.info, `start: ${routerEvent.url}`);
    } else if (routerEvent instanceof NavigationEnd) {
      const analyticsEvent: GTMEventModel = {
        event: 'pageview',
        destination: window.document.location.href,
        identityType: this.gtmService.identityType,
        partnerId: this.gtmService.partnerId,
        roleGroup: this.gtmService.userIotRole,
        selectedDeviceId: this.gtmService.selectedDeviceId,
        selectedGatewayId: this.gtmService.selectedGatewayId,
        userType: this.gtmService.analyticsClientGroup,
        viguideVersion,
      };
      window.dataLayer.push(analyticsEvent);

      this.viLogger.end('router-event-navigation', LogType.info, `end: ${routerEvent.url}`);
    }
  }

  private functionsFiredForMaintainerOrUserHandler(): void {
    this.authStore.isMaintainerOrUser$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((isMaintainer) => isMaintainer)
      )
      .subscribe(() => {
        // Check cached callbacks in localStorage
        this.serviceTicketStore.loadCachedCallbacks();

        // Show updated documents modal
        this.documentsStore.updatedDocuments$
          .pipe(
            takeUntil(this.ngUnsubscribe$),
            filter((documentsUpdated) => documentsUpdated && documentsUpdated.length > 0)
          )
          .subscribe(() => {
            if (this.shouldShowDocumentsDialog) {
              this.documentsDialogRef = this.dialog.open(DocumentsUpdateComponent, {
                width: '535px',
                panelClass: 'documents-updated-dialog',
                disableClose: true,
                data: {},
              });
            }
          });
      });
  }

  private unsubscribeLiveUpdateForNonInstallationPages(url: string): void {
    if (!url.startsWith('/installations')) {
      this.liveUpdatesDataService.unsubscribeAll();
    }
  }

  private registerIcons(): void {
    const data: { name: string; path: string }[] = [
      { name: 'adjust', path: '../assets/icons/adjust.svg' },
      { name: 'shortcut', path: '../assets/icons/shortcut.svg' },
    ];
    data.forEach((icon) => {
      this.matIconRegistry.addSvgIcon(
        icon.name,
        this.domSanitizer.bypassSecurityTrustResourceUrl(icon.path)
      );
    });
  }
}
